import React, { useEffect, useRef, useState } from "react";
import { PaginationProps } from "types";
import { PageArrow, PageNumber, PaginationContainer } from "./index.styles";

interface PaginationComponentProps {
  pagination: PaginationProps;
  onPageChange: (page: number) => void;
  blockSize?: number; // Cuántas páginas mostrar en el bloque (por defecto 7)
  disabled?: boolean;
}

const Pagination: React.FC<PaginationComponentProps> = ({
  pagination,
  onPageChange,
  blockSize = 7,
  disabled = false,
}) => {
  const { current_page, total_pages } = pagination;
  const [blockStart, setBlockStart] = useState(1);
  const activeRef = useRef<HTMLButtonElement>(null);

  // Sincroniza el bloque con la página actual si cambia desde fuera
  useEffect(() => {
    const newBlockStart = Math.max(
      1,
      Math.min(blockStart, total_pages - blockSize + 1)
    );
    if (current_page < blockStart || current_page >= blockStart + blockSize) {
      setBlockStart(Math.max(1, current_page - Math.floor(blockSize / 2)));
    } else if (blockStart !== newBlockStart) {
      setBlockStart(newBlockStart);
    }
  }, [current_page, total_pages, blockSize]);

  // Auto-scroll al botón activo
  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [current_page, blockStart]);

  const endPage = Math.min(total_pages, blockStart + blockSize - 1);
  const pagesArray = [];
  for (let i = blockStart; i <= endPage; i++) {
    pagesArray.push(i);
  }

  const canGoPrevBlock = blockStart > 1;
  const canGoNextBlock = endPage < total_pages;

  const isCurrentInBlock = pagesArray.includes(current_page);

  return (
    <PaginationContainer aria-disabled={disabled}>
      <PageArrow
        disabled={blockStart === 1 || disabled}
        aria-label="First block"
        onClick={() => setBlockStart(1)}
      >
        {'|<<'}
      </PageArrow>
      <PageArrow
        disabled={!canGoPrevBlock || disabled}
        aria-label="Previous block"
        onClick={() => setBlockStart(Math.max(1, blockStart - blockSize))}
      >
        {"<<"}
      </PageArrow>
      {isCurrentInBlock && current_page > blockStart && current_page > 1 && (
        <PageArrow
          disabled={current_page <= 1 || disabled}
          aria-label="Previous page"
          onClick={() => onPageChange(current_page - 1)}
        >
          &lt;
        </PageArrow>
      )}
      {canGoPrevBlock && <span style={{ padding: "0 0.5rem", color: "#fe2f67", fontSize: "1rem", fontWeight: 600 }}>...</span>}
      {pagesArray.map((p) => (
        <PageNumber
          key={p}
          isActive={p === current_page}
          ref={p === current_page ? activeRef : undefined}
          aria-current={p === current_page ? "page" : undefined}
          aria-label={`Go to page ${p}`}
          disabled={disabled}
          onClick={() => {
            if (p !== current_page && !disabled) {
              onPageChange(p);
            }
          }}
        >
          {p}
        </PageNumber>
      ))}
      {canGoNextBlock && (<span style={{ padding: "0 0.5rem", color: "#fe2f67", fontSize: "1rem", fontWeight: 600 }}>...</span>)}
      {isCurrentInBlock && current_page < endPage && current_page < total_pages && (
        <PageArrow
          disabled={current_page >= total_pages || disabled}
          aria-label="Next page"
          onClick={() => onPageChange(current_page + 1)}
        >
          &gt;
        </PageArrow>
      )}
      <PageArrow
        disabled={!canGoNextBlock || disabled}
        aria-label="Next block"
        onClick={() => setBlockStart(blockStart + blockSize)}
      >
        {">>"}
      </PageArrow>
      <PageArrow
        disabled={blockStart + blockSize > total_pages || disabled}
        aria-label="Last block"
        onClick={() => setBlockStart(Math.max(1, total_pages - blockSize + 1))}
      >
        {'>>|'}
      </PageArrow>
    </PaginationContainer>
  );
};

export default Pagination; 